import { useState, useEffect, useMemo } from "react";
import { DataTable, StatusBadge, ConfirmDialog } from "../../components/dashboard";
import {
  listAllInstances,
  adminForceStop,
  adminForceStart,
  adminForceDelete,
  adminListSelfHostedInstances,
  adminUpdateSelfHostedInstance,
  adminDeleteSelfHostedInstance,
} from "../../lib/api/cloud";
import type { InstanceWithUser, InstanceStatus, AdminSelfHostedInstance, SelfHostedStatus } from "../../types/cloud";

type CloudStatusFilter = "all" | InstanceStatus;
type SelfHostedStatusFilter = "all" | SelfHostedStatus;
type SelfHostedModeFilter = "all" | "public-ip" | "tunneled";
type Tab = "cloud" | "self-hosted";

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function InstanceManagement() {
  const [tab, setTab] = useState<Tab>("cloud");

  // Cloud instances state
  const [instances, setInstances] = useState<InstanceWithUser[]>([]);
  const [cloudLoading, setCloudLoading] = useState(true);
  const [cloudFilter, setCloudFilter] = useState<CloudStatusFilter>("all");
  const [actionLoading, setActionLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<InstanceWithUser | null>(null);

  // Self-hosted instances state
  const [shInstances, setShInstances] = useState<AdminSelfHostedInstance[]>([]);
  const [shLoading, setShLoading] = useState(true);
  const [shStatusFilter, setShStatusFilter] = useState<SelfHostedStatusFilter>("all");
  const [shModeFilter, setShModeFilter] = useState<SelfHostedModeFilter>("all");
  const [shDeleteTarget, setShDeleteTarget] = useState<AdminSelfHostedInstance | null>(null);
  const [shActionLoading, setShActionLoading] = useState(false);

  // ─── Cloud Instances ────────────────────────────────────────────────

  const fetchCloudData = async () => {
    try {
      const res = await listAllInstances();
      setInstances(res.instances);
    } catch {
      // Error handling
    } finally {
      setCloudLoading(false);
    }
  };

  useEffect(() => {
    fetchCloudData();
  }, []);

  const filteredInstances = useMemo(() => {
    if (cloudFilter === "all") return instances;
    return instances.filter((i) => i.status === cloudFilter);
  }, [instances, cloudFilter]);

  const handleCloudAction = async (id: string, action: "stop" | "start") => {
    setActionLoading(true);
    try {
      if (action === "stop") await adminForceStop(id);
      else await adminForceStart(id);
      await fetchCloudData();
    } catch {
      // Error handling
    } finally {
      setActionLoading(false);
    }
  };

  const handleCloudDelete = async () => {
    if (!deleteTarget) return;
    setActionLoading(true);
    try {
      await adminForceDelete(deleteTarget.id);
      await fetchCloudData();
    } catch {
      // Error handling
    } finally {
      setActionLoading(false);
      setDeleteTarget(null);
    }
  };

  // ─── Self-Hosted Instances ──────────────────────────────────────────

  const fetchShData = async () => {
    try {
      const res = await adminListSelfHostedInstances();
      setShInstances(res.instances);
    } catch {
      // Error handling
    } finally {
      setShLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "self-hosted" && shInstances.length === 0) {
      fetchShData();
    }
  }, [tab]);

  const filteredShInstances = useMemo(() => {
    let filtered = shInstances;
    if (shStatusFilter !== "all") {
      filtered = filtered.filter((i) => i.status === shStatusFilter);
    }
    if (shModeFilter !== "all") {
      filtered = filtered.filter((i) => i.mode === shModeFilter);
    }
    return filtered;
  }, [shInstances, shStatusFilter, shModeFilter]);

  const handleShDisable = async (id: string, currentlyDisabled: boolean) => {
    setShActionLoading(true);
    try {
      await adminUpdateSelfHostedInstance(id, {
        status: currentlyDisabled ? "active" : "disabled",
      });
      await fetchShData();
    } catch {
      // Error handling
    } finally {
      setShActionLoading(false);
    }
  };

  const handleShDelete = async () => {
    if (!shDeleteTarget) return;
    setShActionLoading(true);
    try {
      await adminDeleteSelfHostedInstance(shDeleteTarget.id);
      await fetchShData();
    } catch {
      // Error handling
    } finally {
      setShActionLoading(false);
      setShDeleteTarget(null);
    }
  };

  // ─── Tab switch handler ─────────────────────────────────────────────

  const handleTabChange = (newTab: Tab) => {
    setTab(newTab);
    if (newTab === "self-hosted") {
      fetchShData();
    }
  };

  // ─── Cloud Instances Render ─────────────────────────────────────────

  const cloudFilterButtons: { label: string; value: CloudStatusFilter }[] = [
    { label: "All", value: "all" },
    { label: "Running", value: "running" },
    { label: "Stopped", value: "stopped" },
    { label: "Provisioning", value: "provisioning" },
    { label: "Error", value: "error" },
  ];

  const cloudColumns = [
    {
      key: "subdomain",
      label: "Subdomain",
      sortable: true,
      render: (i: InstanceWithUser) => (
        <a
          href={i.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-400 hover:text-blue-300 font-mono text-sm"
        >
          {i.subdomain}
        </a>
      ),
    },
    {
      key: "userEmail",
      label: "User",
      sortable: true,
      render: (i: InstanceWithUser) => (
        <div>
          <span className="text-sm">{i.userEmail}</span>
          {i.userName && <span className="text-[#666] text-xs ml-1">({i.userName})</span>}
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (i: InstanceWithUser) => <StatusBadge status={i.status} />,
    },
    {
      key: "providerIp",
      label: "IP",
      render: (i: InstanceWithUser) => (
        <span className="font-mono text-sm text-[#999]">{i.providerIp ?? "—"}</span>
      ),
    },
    {
      key: "createdAt",
      label: "Created",
      sortable: true,
      render: (i: InstanceWithUser) => (
        <span className="text-[#999] text-sm">{formatRelativeTime(i.createdAt)}</span>
      ),
    },
    {
      key: "lastHealthAt",
      label: "Health",
      sortable: true,
      render: (i: InstanceWithUser) => {
        if (!i.lastHealthAt) return <span className="text-[#666] text-sm">—</span>;
        const age = Date.now() - new Date(i.lastHealthAt).getTime();
        const isHealthy = age < 120000; // 2 minutes
        return (
          <span className={`text-sm ${isHealthy ? "text-green-400" : "text-yellow-400"}`}>
            {formatRelativeTime(i.lastHealthAt)}
          </span>
        );
      },
    },
    {
      key: "actions",
      label: "Actions",
      render: (i: InstanceWithUser) => (
        <div className="flex gap-2">
          {i.status === "running" && (
            <button
              onClick={() => handleCloudAction(i.id, "stop")}
              disabled={actionLoading}
              className="px-2.5 py-1 bg-yellow-500/10 text-yellow-400 rounded text-xs font-medium hover:bg-yellow-500/20 disabled:opacity-40"
            >
              Force Stop
            </button>
          )}
          {i.status === "stopped" && (
            <button
              onClick={() => handleCloudAction(i.id, "start")}
              disabled={actionLoading}
              className="px-2.5 py-1 bg-green-500/10 text-green-400 rounded text-xs font-medium hover:bg-green-500/20 disabled:opacity-40"
            >
              Force Start
            </button>
          )}
          {!["running", "starting"].includes(i.status) && (
            <button
              onClick={() => setDeleteTarget(i)}
              disabled={actionLoading}
              className="px-2.5 py-1 bg-red-500/10 text-red-400 rounded text-xs font-medium hover:bg-red-500/20 disabled:opacity-40"
            >
              Force Delete
            </button>
          )}
        </div>
      ),
    },
  ];

  const renderCloudInstances = () => {
    if (cloudLoading) {
      return (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    return (
      <>
        <div className="bg-[#1a1a1a] border border-[#333] rounded-lg">
          <DataTable
            columns={cloudColumns}
            data={filteredInstances}
            keyFn={(i) => i.id}
            emptyMessage="No cloud instances found"
            filters={
              <div className="flex gap-2 px-4 pt-4">
                {cloudFilterButtons.map((btn) => (
                  <button
                    key={btn.value}
                    onClick={() => setCloudFilter(btn.value)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors
                      ${
                        cloudFilter === btn.value
                          ? "bg-blue-500/10 text-blue-400"
                          : "text-[#999] hover:text-[#f5f5f5] hover:bg-[#111]"
                      }`}
                  >
                    {btn.label}
                  </button>
                ))}
              </div>
            }
          />
        </div>

        <ConfirmDialog
          open={deleteTarget !== null}
          title="Delete Instance"
          message={`Are you sure you want to force-delete the instance "${deleteTarget?.subdomain}"? This will remove the VM, DNS records, and all data. This cannot be undone.`}
          confirmText="Force Delete"
          danger
          confirmInput={deleteTarget?.subdomain}
          onConfirm={handleCloudDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      </>
    );
  };

  // ─── Self-Hosted Instances Render ───────────────────────────────────

  const shStatusFilterButtons: { label: string; value: SelfHostedStatusFilter }[] = [
    { label: "All", value: "all" },
    { label: "Active", value: "active" },
    { label: "Connecting", value: "connecting" },
    { label: "Disconnected", value: "disconnected" },
    { label: "Disabled", value: "disabled" },
    { label: "Pending", value: "pending" },
  ];

  const shModeFilterButtons: { label: string; value: SelfHostedModeFilter }[] = [
    { label: "All Modes", value: "all" },
    { label: "Public IP", value: "public-ip" },
    { label: "Tunneled", value: "tunneled" },
  ];

  const shColumns = [
    {
      key: "subdomain",
      label: "Subdomain",
      sortable: true,
      render: (i: AdminSelfHostedInstance) => (
        <a
          href={i.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-400 hover:text-blue-300 font-mono text-sm"
        >
          {i.subdomain}
        </a>
      ),
    },
    {
      key: "userEmail",
      label: "User",
      sortable: true,
      render: (i: AdminSelfHostedInstance) => (
        <div>
          <span className="text-sm">{i.userEmail}</span>
          {i.userName && <span className="text-[#666] text-xs ml-1">({i.userName})</span>}
        </div>
      ),
    },
    {
      key: "mode",
      label: "Mode",
      sortable: true,
      render: (i: AdminSelfHostedInstance) => (
        <span className={`text-xs font-medium px-2 py-0.5 rounded ${
          i.mode === "tunneled"
            ? "bg-purple-500/10 text-purple-400"
            : "bg-cyan-500/10 text-cyan-400"
        }`}>
          {i.mode === "tunneled" ? "Tunneled" : "Public IP"}
        </span>
      ),
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (i: AdminSelfHostedInstance) => <StatusBadge status={i.status} />,
    },
    {
      key: "targetIp",
      label: "Target",
      render: (i: AdminSelfHostedInstance) => {
        if (i.mode === "public-ip" && i.targetIp) {
          return <span className="font-mono text-sm text-[#999]">{i.targetIp}</span>;
        }
        if (i.mode === "tunneled" && i.tunnelPort) {
          return <span className="font-mono text-sm text-[#999]">:{i.tunnelPort}</span>;
        }
        return <span className="text-[#666] text-sm">—</span>;
      },
    },
    {
      key: "lastPingAt",
      label: "Last Ping",
      sortable: true,
      render: (i: AdminSelfHostedInstance) => {
        if (!i.lastPingAt) return <span className="text-[#666] text-sm">—</span>;
        const age = Date.now() - new Date(i.lastPingAt).getTime();
        const isRecent = age < 120000; // 2 minutes
        return (
          <span className={`text-sm ${isRecent ? "text-green-400" : "text-yellow-400"}`}>
            {formatRelativeTime(i.lastPingAt)}
          </span>
        );
      },
    },
    {
      key: "createdAt",
      label: "Created",
      sortable: true,
      render: (i: AdminSelfHostedInstance) => (
        <span className="text-[#999] text-sm">{formatRelativeTime(i.createdAt)}</span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (i: AdminSelfHostedInstance) => (
        <div className="flex gap-2">
          {i.status === "disabled" ? (
            <button
              onClick={() => handleShDisable(i.id, true)}
              disabled={shActionLoading}
              className="px-2.5 py-1 bg-green-500/10 text-green-400 rounded text-xs font-medium hover:bg-green-500/20 disabled:opacity-40"
            >
              Enable
            </button>
          ) : (
            <button
              onClick={() => handleShDisable(i.id, false)}
              disabled={shActionLoading}
              className="px-2.5 py-1 bg-yellow-500/10 text-yellow-400 rounded text-xs font-medium hover:bg-yellow-500/20 disabled:opacity-40"
            >
              Disable
            </button>
          )}
          <button
            onClick={() => setShDeleteTarget(i)}
            disabled={shActionLoading}
            className="px-2.5 py-1 bg-red-500/10 text-red-400 rounded text-xs font-medium hover:bg-red-500/20 disabled:opacity-40"
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  const renderSelfHostedInstances = () => {
    if (shLoading) {
      return (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    return (
      <>
        <div className="bg-[#1a1a1a] border border-[#333] rounded-lg">
          <DataTable
            columns={shColumns}
            data={filteredShInstances}
            keyFn={(i) => i.id}
            emptyMessage="No self-hosted instances found"
            filters={
              <div className="flex gap-4 px-4 pt-4">
                <div className="flex gap-2">
                  {shStatusFilterButtons.map((btn) => (
                    <button
                      key={btn.value}
                      onClick={() => setShStatusFilter(btn.value)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors
                        ${
                          shStatusFilter === btn.value
                            ? "bg-blue-500/10 text-blue-400"
                            : "text-[#999] hover:text-[#f5f5f5] hover:bg-[#111]"
                        }`}
                    >
                      {btn.label}
                    </button>
                  ))}
                </div>
                <div className="w-px bg-[#333]" />
                <div className="flex gap-2">
                  {shModeFilterButtons.map((btn) => (
                    <button
                      key={btn.value}
                      onClick={() => setShModeFilter(btn.value)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors
                        ${
                          shModeFilter === btn.value
                            ? "bg-purple-500/10 text-purple-400"
                            : "text-[#999] hover:text-[#f5f5f5] hover:bg-[#111]"
                        }`}
                    >
                      {btn.label}
                    </button>
                  ))}
                </div>
              </div>
            }
          />
        </div>

        <ConfirmDialog
          open={shDeleteTarget !== null}
          title="Delete Self-Hosted Instance"
          message={`Are you sure you want to delete the self-hosted instance "${shDeleteTarget?.subdomain}"? This will remove the routing configuration and all associated data. This cannot be undone.`}
          confirmText="Delete"
          danger
          confirmInput={shDeleteTarget?.subdomain}
          onConfirm={handleShDelete}
          onCancel={() => setShDeleteTarget(null)}
        />
      </>
    );
  };

  // ─── Main Render ────────────────────────────────────────────────────

  const tabButtons: { label: string; value: Tab }[] = [
    { label: "Cloud Instances", value: "cloud" },
    { label: "Self-Hosted Instances", value: "self-hosted" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#f5f5f5]">Instance Management</h1>
        <p className="text-[#999] text-sm mt-1">
          {tab === "cloud"
            ? `${instances.length} total cloud instances`
            : `${shInstances.length} total self-hosted instances`}
        </p>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-2 mb-6">
        {tabButtons.map((btn) => (
          <button
            key={btn.value}
            onClick={() => handleTabChange(btn.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors
              ${
                tab === btn.value
                  ? "bg-[#1a1a1a] text-[#f5f5f5] border border-[#333]"
                  : "text-[#999] hover:text-[#f5f5f5] hover:bg-[#111]"
              }`}
          >
            {btn.label}
          </button>
        ))}
      </div>

      {tab === "cloud" ? renderCloudInstances() : renderSelfHostedInstances()}
    </div>
  );
}
